/**
 * 统一的API配置工具
 * 动态检测访问方式，自动选择正确的后端地址
 */

/**
 * 判断是否为生产环境
 * - 通过域名访问 (包含 demo, uat, prod 等)
 * - 非 localhost/127.0.0.1
 */
const isProduction = (): boolean => {
  const hostname = window.location.hostname;
  // localhost 或 127.0.0.1 是开发环境
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }
  // IP 地址直接访问也视为开发环境
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
    return false;
  }
  // 其他情况（域名访问）视为生产环境
  return true;
};

/**
 * 获取动态API基础地址
 * - 生产环境（域名访问）→ /api/v1 (通过 Nginx 代理)
 * - 开发环境 → http://localhost:9050/api/v1
 */
export const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;

  // 生产环境：通过 Nginx 代理，使用相对路径
  if (isProduction()) {
    return '/api/v1';
  }

  // 开发环境：localhost 直接访问后端
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:9050/api/v1';
  }

  // 开发环境：IP 访问
  return `http://${hostname}:9050/api/v1`;
};

/**
 * 获取API基础地址（不带/api/v1前缀）
 */
export const getApiBaseUrlWithoutPrefix = (): string => {
  const hostname = window.location.hostname;

  // 生产环境：使用相对路径
  if (isProduction()) {
    return '';
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:9050';
  }

  return `http://${hostname}:9050`;
};

/**
 * 获取WebSocket基础地址
 */
export const getWebSocketBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  // 生产环境：使用当前域名
  if (isProduction()) {
    return `${protocol}//${hostname}`;
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'ws://localhost:9050';
  }

  return `ws://${hostname}:9050`;
};

/**
 * 获取公开访问地址
 * 用于生成问卷链接、二维码等需要外部访问的URL
 * 优先使用环境变量配置，否则使用当前页面origin
 */
export const getPublicUrl = (): string => {
  // 优先使用环境变量配置的公开地址
  const envPublicUrl = process.env.REACT_APP_PUBLIC_URL;
  if (envPublicUrl && envPublicUrl.trim()) {
    // 移除末尾的斜杠
    return envPublicUrl.replace(/\/+$/, '');
  }

  // 默认使用当前页面的origin
  return window.location.origin;
};

/**
 * 生成问卷访问链接
 * @param channelCode 渠道代码
 * @returns 完整的问卷访问URL
 */
export const getQuestionnaireUrl = (channelCode: string): string => {
  const baseUrl = getPublicUrl();
  return `${baseUrl}/questionnaire?channel=${encodeURIComponent(channelCode)}`;
};
